import * as Sentry from '@sentry/react';
import '@agissc/ktag-map/dist/style.css';
import './App.scss';
import React from 'react';
import { Routes, Route, BrowserRouter as Router } from 'react-router-dom';
import { WebWrapper, PageWrapper, Sprite } from '@ktag/styleguide/dist/react';
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import {
  IconsContext,
  IconsContextProps,
  LoginStateHandler,
  WebMapStateProvider,
} from '@agissc/ktag-map';
import { AuthenticatedUserStore } from '@ktag/commons';
import Heading from './components/heading/Heading';
import GisBrowser from './pages/GisBrowser';
import Entdecken from './pages/Entdecken';
import config from './utils/AppConfig';
import useAppState from './AppStore';
import ChevronUp from './assets/icons/chevron-up.svg?react';
import ChevronDown from './assets/icons/chevron-down.svg?react';
import MinusCircle from './assets/icons/minus-circle.svg?react';
import RedirectLink from './assets/icons/redirect-link.svg?react';
import locales from './assets/locales/agisviewer+core.de.json';
import HandPoint from './assets/icons/hand-point.svg?react';
import Line from './assets/icons/stroke.svg?react';
import { SessionExpiredNotification } from './utils/tracking/SessionExpiredNotification';
import { ExternalViewerAPIProvider } from './ExternalViewerApi/ExternalViewerAPIProvider';

const releaseVersion = import.meta.env.VITE_APP_VERSION || 'N/A';

Sentry.init({
  dsn: 'https://9825264c4fee58b207cab22c487f8e92@o4506540662128640.ingest.us.sentry.io/4509032949874688',
  integrations: [Sentry.browserTracingIntegration()],
  ignoreErrors: [
    // Crypto wallet extension errors (user environment, not app issues)
    'Failed to connect to MetaMask',
    'Talisman extension has not been configured yet. Please continue with onboarding.',
    // Performance warnings (also adding here as fallback)
    'Large Render Blocking Asset',
    'Large HTTP payload',
    'HTTP/1.1 Overhead',
    // DOM manipulation errors caused by browser extensions (translators, ad blockers, etc.)
    "Failed to execute 'insertBefore' on 'Node'",
    "Failed to execute 'removeChild' on 'Node'",
  ],
  ignoreTransactions: [
    // Performance-related transaction names to ignore
    'Large Render Blocking Asset',
    'Large HTTP payload',
    'HTTP/1.1 Overhead',
  ],
  tracesSampleRate: 0.2, //  Capture 100% of the transactions
  tracePropagationTargets: [
    'localhost',
    /^https:\/\/ag\.ch\/geoportal\/apps\/onlinekarten\/.*/,
  ],
  release: releaseVersion,
  environment: config.sentryEnv,
  enabled: config.sentryEnv !== 'dev',
  sendDefaultPii: false, // Do not send personal data
  beforeSend: (event: Sentry.ErrorEvent) => {
    if (event.user) {
      // eslint-disable-next-line no-param-reassign
      event.user.ip_address = '{{auto}}'; // Anonymize IP Address
    }
    return event;
  },
});

const Icons = {
  extraIcons: {
    'chevron-up': ChevronUp,
    'chevron-down': ChevronDown,
    'minus-circle': MinusCircle,
    'redirect-link': RedirectLink,
    'hand-point': HandPoint,
    stroke: Line,
  } as Record<string, React.FunctionComponent<React.SVGProps<SVGSVGElement>>>,
  ExternalIconComponent: Sprite,
} as IconsContextProps;

i18n.use(initReactI18next).init({
  resources: locales,
  lng: 'de',
  fallbackLng: 'de',
  ns: ['agisviewer', 'core'],
  defaultNS: 'agisviewer',
});

const initDataAttributes = () => {
  const dataAttributes = {
    'data-login-api': config.apis.login,
    'data-profile-api': config.apis.profile,
    'data-auth-api': config.apis.auth,
    'data-context-switch-api': config.apis.contextSwitch,
  };

  Object.entries(dataAttributes).forEach(([key, value]) => {
    document.documentElement.setAttribute(key, value);
  });
};

initDataAttributes();

const queryClient = new QueryClient();

const App = () => {
  return (
    <AuthenticatedUserStore>
      <WebMapStateProvider useLocalState={useAppState}>
        <IconsContext.Provider value={Icons}>
          <Router basename={config.basename}>
            <QueryClientProvider client={queryClient}>
              <ExternalViewerAPIProvider>
                <LoginStateHandler />
                <SessionExpiredNotification />
                <WebWrapper>
                  <PageWrapper>
                    <Heading />
                    <PageWrapper.Main>
                      <div className="App">
                        <Routes>
                          <Route path="/" element={<GisBrowser />} />
                          <Route path="/discover" element={<Entdecken />} />
                        </Routes>
                      </div>
                    </PageWrapper.Main>
                  </PageWrapper>
                </WebWrapper>
              </ExternalViewerAPIProvider>
            </QueryClientProvider>
          </Router>
        </IconsContext.Provider>
      </WebMapStateProvider>
    </AuthenticatedUserStore>
  );
};

export default App;
